import { Provider } from '@angular/core';
import { Store } from '@ngrx/store';

import { extractValueFromStore } from '@ninety/platform/services/state-util';
import { FeatureFlagKeys } from '@ninety/platform/state/app-entities/feature-flag/feature-flag-state.model';
import { selectFeatureFlag } from '@ninety/platform/state/app-entities/feature-flag/feature-flag-state.selectors';
import {
  selectCurrentUser,
  selectCurrentUserId,
} from '@ninety/platform/state/app-entities/users/users-state.selectors';
import {
  CONTACT_CARD_ENABLED_STREAM,
  CURRENT_USER_ID_PROVIDER,
  CURRENT_USER_PROVIDER,
  CurrentUserIdProvider,
  CurrentUserProvider,
  USER_ROSTER,
} from '@ninety/ui/core';
import { UserService } from '@ninety/ui/legacy/core/services/user.service';

/**
 * Binds the tokens consumed by `UserSelectComponent` in `@ninety/ui/core` to their platform-backed
 * implementations: the user roster (`UserService`), snapshot reads of `selectCurrentUser` and
 * `selectCurrentUserId` — the same `extractValueFromStore` calls the component used to make itself — and
 * the contact-card feature flag (`selectFeatureFlag(FeatureFlagKeys.enableContactCard)`, exactly what
 * `FeatureFlagFacade.getFlag` does). The component stays free of `@ngrx/store`, of
 * `@ninety/platform/state` and of `UserService`'s transitive graph, none of which can be compiled into
 * the public package, so this composition-root wiring is the single place that depends on both sides.
 *
 * `COMPANY_LANGUAGE_STREAM`, which the component also consumes, is deliberately NOT bound here —
 * `FOLLOWERS_PROVIDERS` already binds it, and re-binding would let the last registration silently win.
 * (DEV-26530 part 16)
 */
export const USER_SELECT_PROVIDERS: Provider[] = [
  { provide: USER_ROSTER, useExisting: UserService },
  {
    provide: CURRENT_USER_PROVIDER,
    useFactory:
      (store: Store): CurrentUserProvider =>
      () =>
        extractValueFromStore(store, selectCurrentUser),
    deps: [Store],
  },
  {
    provide: CURRENT_USER_ID_PROVIDER,
    useFactory:
      (store: Store): CurrentUserIdProvider =>
      () =>
        extractValueFromStore(store, selectCurrentUserId),
    deps: [Store],
  },
  {
    provide: CONTACT_CARD_ENABLED_STREAM,
    useFactory: (store: Store) => store.select(selectFeatureFlag(FeatureFlagKeys.enableContactCard)),
    deps: [Store],
  },
];
